// Stock's Catalog and prices (Catalog.tsx): what it asks stock.catalog for, and the words around it.
// The catalog is the price book's own parts (its materials and equipment, never its services), with what
// stock keeps on each: the same records as Setup › Price book, so nothing is copied. Their price, cost
// and name are changed there (pricebook.updateItem, the owner and managers); what the vendor sells it in,
// its vendor and bin and whether it's counted here (stock.setItem, who keeps the stock).
import type { CatalogPart } from '@flowology/ui-web';
import { OFFICE_PERMS } from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';

export type CatalogType = 'all' | 'material' | 'equipment';

export const CATALOG_TYPES: { type: CatalogType; label: string }[] = [
  { type: 'all', label: 'Every part' },
  { type: 'material', label: 'Materials' },
  { type: 'equipment', label: 'Equipment' },
];

/** One page of stock.catalog's answer (packages/modules inventory/catalog.ts). */
export interface CatalogPage {
  parts: CatalogPart[];
  next: string | null;
}

export interface Category {
  id: string;
  name: string;
  parentId: string | null;
  active: boolean;
}

/** stock.catalog's input for a search, a category and a type. */
export function catalogInput(
  query: string,
  categoryId: string | null,
  type: CatalogType,
): Record<string, unknown> {
  const q = query.trim();
  return {
    ...(q ? { query: q } : {}),
    ...(categoryId ? { categoryId } : {}),
    ...(type !== 'all' ? { type } : {}),
  };
}

/** The categories to pick from: those still offered, a sub-category after its parent's name. */
export function categoryOptions(cats: Category[]): { id: string; label: string }[] {
  const name = new Map(cats.map((c) => [c.id, c.name]));
  return cats
    .filter((c) => c.active)
    .map((c) => ({
      id: c.id,
      label: c.parentId && name.has(c.parentId) ? `${name.get(c.parentId)} › ${c.name}` : c.name,
    }));
}

/** Setup › Price book is the office's (Settings): the warehouse has no Setup to go to. */
export const opensPriceBook = (held: readonly string[]): boolean => holdsAny(held, OFFICE_PERMS);

/** The price book's own link (#/settings/pricebook), and the catalog's from it (#/catalog). */
export const PRICE_BOOK_LINK = '#/settings/pricebook';
export const CATALOG_LINK = '#/catalog';
